<script lang="ts">
const NATIVE_PICKER_TAP_TARGET =
        '[&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0'
const NATIVE_VALUE_LINE_BOX = '[&::-webkit-datetime-edit]:p-0 [&::-webkit-datetime-edit]:leading-none'
const NATIVE_VALUE_ALIGNMENT = '[&::-webkit-date-and-time-value]:text-left'
</script>
<script setup lang="ts">
    import VueDatePicker from '@vuepic/vue-datepicker';
    import { FormInputProps, FormInputData } from '~~/composables/useFormInput';
    import { useIsMobileLayout } from '~/composables/useIsMobileLayout';
    import '@vuepic/vue-datepicker/dist/main.css';

    const props = withDefaults(
        defineProps<
            FormInputProps & {
                alignment?: 'top-full' | 'top-0' | 'bottom-0' | 'bottom-full';
                position?: 'left-full' | 'left-0' | 'right-0' | 'right-full' | 'center-x' | 'screen-center';
                minDate?: Date | null;
                disabled?: boolean;
            }
        >(),
        {
            position: 'left-0',
            alignment: 'top-full',
            isAbsolute: true,
            minDate: null,
        }
    );
    const inputData: FormInputData = useFormInput(props);

    // Mobile hands the calendar to the OS (`<input type="date">`). The `Submenu` popover
    // cannot work inside a sheet: it is taller than the drawer's scrollable area and,
    // being `absolute`, never grows it. Nor can it escape — `UiActionSheet`'s
    // `DrawerContent` carries a `transform`, which makes it the containing block of every
    // `fixed` descendant, so `Submenu`'s own `screen-center` would only centre it inside
    // the drawer.
    //
    // The boundary is `useIsMobileLayout` (`< lg`) rather than a touch check or `md`
    // because it has to be the same one `UiResponsiveModal` switches on: that is what
    // turns a modal into the drawer that traps the popover.
    const isMobile = useIsMobileLayout();

    const date = computed({
        get() {
            return moment(inputData.model.value).toDate();
        },
        set(value) {
            inputData.form.set(inputData.inputName, moment(value).toDateTimeString());
        },
    });
    const formattedDate = computed(() => (inputData.model.value ? moment(inputData.model.value).toEUDateString() : ''));

    // `<input type="date">` only speaks `YYYY-MM-DD`, which is what `toDateString()`
    // formats — the model itself keeps the datetime string both branches write.
    const isoDate = computed(() => (inputData.model.value ? moment(inputData.model.value).toDateString() : ''));
    const isoMinDate = computed(() => (props.minDate ? moment(props.minDate).toDateString() : undefined));

    const onNativeChange = (event: Event) => {
        const input = event.target as HTMLInputElement;
        // Mirrors the desktop picker's `:clearable="false"`: an emptied field is
        // refused and snapped back rather than writing a null the other branch could
        // never produce. The OS pickers offer no clear button, but the keyboard does.
        if (!input.value) {
            input.value = isoDate.value;
            return;
        }
        inputData.form.set(inputData.inputName, moment(input.value).toDateTimeString());
    };

    // Chrome only opens the picker from its own indicator, so the indicator is
    // stretched over the whole field and made invisible: a tap anywhere on the row
    // opens it, while our calendar icon and chevron stay the visible chrome (iOS
    // Safari renders no indicator and opens on any tap regardless). Cheaper and more
    // predictable than calling `showPicker()`, which throws where it is unsupported.
    

    // The UA sizes the value's line box at 1.05 rather than following the field's
    // `leading-none`, which leaves this field taller than the text inputs beside it.
    // Honouring the same intent closes most of the gap; the last pixel lives in the UA's
    // shadow tree, out of CSS reach, and pinning a hard height there would drift with the
    // font.
    

    // iOS centres the value of a native date field, which puts it out of line with every
    // other input beside it. `text-left` on the input does not reach it: WebKit renders
    // the value inside `::-webkit-date-and-time-value`, a shadow-tree part that centres
    // itself rather than inheriting. Aligning that part is the only way in — and it is
    // the part iOS actually uses, where `::-webkit-datetime-edit` above is what the
    // desktop engines render.
    
</script>

<template>
    <!-- No `@label-clicked` on this branch: the label's own `for` focuses the input and
         the OS takes it from there, so there is nothing to open by hand. -->
    <FormElement
        v-if="isMobile"
        :input-data="inputData"
    >
        <div class="relative">
            <i class="fa-solid fa-calendar absolute left-2 top-1/2 z-10 -translate-y-1/2 pointer-events-none text-sm text-gray-400"></i>
            <input
                :id="inputData.inputId.value"
                type="date"
                :required="inputData.required"
                :class="[inputData.className.value, 'cursor-pointer appearance-none pl-8 pr-8 text-left', NATIVE_VALUE_LINE_BOX, NATIVE_VALUE_ALIGNMENT, NATIVE_PICKER_TAP_TARGET]"
                :name="inputData.props.inputName"
                :value="isoDate"
                :min="isoMinDate"
                :disabled="disabled"
                @change="onNativeChange"
            />
            <i class="fa-solid fa-chevron-down absolute right-2 top-1/2 z-10 -translate-y-1/2 pointer-events-none text-xs text-gray-400"></i>
        </div>
    </FormElement>

    <Submenu
        v-else
        :container="$theme('submenu.container.withoutPosition', 'p-4')"
        without-max-height
        :position="position"
        :alignment="alignment"
    >
        <template #default="{ toggle, show, hide }">
            <FormElement
                :input-data="inputData"
                @label-clicked="disabled ? null : show()"
            >
                <slot
                    name="input"
                    :toggle="toggle"
                    :show="show"
                    :hide="hide"
                >
                    <div class="relative">
                        <i class="fa-solid fa-calendar absolute left-2 md:left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10 text-sm"></i>
                        <input
                            :id="inputData.inputId.value"
                            :required="inputData.required"
                            :placeholder="inputData.placeholder.value"
                            :class="[inputData.className.value, '!pl-8 !md:pl-10 pr-8 md:pr-10 cursor-pointer']"
                            :name="inputData.props.inputName"
                            :value="formattedDate"
                            :disabled="disabled"
                            @focus="disabled ? null : show()"
                        />
                        <i class="fa-solid fa-chevron-down absolute right-2 md:right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10 text-xs"></i>
                    </div>
                </slot>
            </FormElement>
        </template>
        <template #submenu="{ hide }">
            <slot
                name="datepicker"
                :hide="hide"
            >
                <VueDatePicker
                    v-model="date"
                    format="dd/MM/yyyy"
                    :enable-time-picker="false"
                    hide-input-icon
                    inline
                    :min-date="minDate"
                    :locale="useGetCurrentMomentLocale()"
                    auto-apply
                    :clearable="false"
                    :required="inputData.required.value"
                    :placeholder="inputData.placeholder.value"
                    @update:model-value="hide"
                />
            </slot>
        </template>
    </Submenu>
</template>
